import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

const API_URL = process.env.REACT_APP_BASE_URL;
const TOKEN = "a94c7ac0083ae190f0fe61143a7547799b5334c4";

const headers = {
  Authorization: `Token ${TOKEN}`,
  "Content-Type": "application/json",
};

export const fetchTermsAndConditions = createAsyncThunk(
  "terms-and-condition/fetchTermsAndConditions",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search.trim());
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.TERMS_AND_CONDITIONS?.LIST}?${params.toString()}`,
        body,
      );
      return {
        data: response.data.data || response.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch Terms And Conditions");
    }
  },
);

export const fetchTermsAndConditionById = createAsyncThunk(
  "terms-and-condition/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.DETAIL(id),
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Terms And Condition by ID",
      );
    }
  },
);

export const fetchTermsAndConditionByDropDown = createAsyncThunk(
  "terms-and-condition/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.DROPDOWN,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Terms And Condition list",
      );
    }
  },
);

export const updateTermsAndCondition = createAsyncThunk(
  "terms-and-condition/updateTermsAndCondition",
  async (data: any, { rejectWithValue }) => {
    console.log("data in update terms and condition", data);
    try {
      const response: any = await Axios.put(
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.UPDATE(
          data.terms_and_condition_id,
        ),
        data.payload,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update Terms And Condition",
      );
    }
  },
);

export const updateStatusInTermsAndCondition = createAsyncThunk(
  "terms-and-condition/updateStatusInTermsAndCondition",
  async (data: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: data.is_active };
      const response: any = await Axios.patch(
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.STATUS_UPDATE(data.id),
        payload,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in Terms And Condition",
      );
    }
  },
);

export const addTermsAndCondition = createAsyncThunk(
  "terms-and-condition/addTermsAndCondition",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.CREATE,
        data,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to add Terms And Condition",
      );
    }
  },
);

export const deleteTermsAndCondition = createAsyncThunk(
  "terms-and-condition/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      await Axios.delete(API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.DELETE(id));
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const fetchTermsAndConditionsOptions = createAsyncThunk(
  "terms-and-condition/fetchOptionsByType",
  async (type: string, { rejectWithValue }) => {
    console.log("type", type);
    try {
      const response = await Axios.get(
        `${API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.OPTIONS_DROPDOWN_BY_TYPE(type)}`,
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data ||
          "Failed to fetch Terms And Condition options by type",
      );
    }
  },
);

export const exportTermsAndConditionCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.MASTERS.TERMS_AND_CONDITIONS.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename: `${
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "Terms And Conditions List"
    }_${new Date().toISOString().split("T")[0]}.csv`,
  };
};
